/**
 * Dashboard statistics hook
 * Loads and manages dashboard statistics with real-time updates and error handling
 */

import { useEffect, useState, useCallback } from 'react';
import type { DashboardStats } from '../services/stats.service';
import { getDashboardStats } from '../services/stats.service';

export const useDashboardStats = (enableAutoRefresh = true, refreshInterval = 30000) => {
  const [stats, setStats] = useState<DashboardStats>({
    totalCompanies: 0,
    totalUsers: 0,
    activeOneTimeJobs: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDashboardStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();

    // Set up auto-refresh interval if enabled
    if (enableAutoRefresh) {
      const intervalId = setInterval(loadStats, refreshInterval);
      return () => clearInterval(intervalId);
    }
  }, [loadStats, enableAutoRefresh, refreshInterval]);

  return {
    stats,
    loading,
    error,
    refreshStats: loadStats,
  };
};
